import React from 'react'
import { useTranslation } from 'react-i18next'
import Paper from '@mui/material/Paper'
import { Link, useLoaderData, useRevalidator } from 'react-router'
import { formatDateYYYYMMDDhhmmss } from '../../../../packages/utils/numberFormatters'
import { useAppLocale } from '../../../../i18n/localeUtils'
import Grid from '@mui/material/Grid'
import { CompanyPressReleases } from './CompanyPressReleases'
import { Helmet } from '@dr.pogodin/react-helmet'
import { ClockIcon, FileIcon } from 'lucide-react'
import Box from '@mui/material/Box'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListSubheader from '@mui/material/ListSubheader'
import ListItemText from '@mui/material/ListItemText'
import { PressReleaseRelatedTransactionsTags } from '../../../../components/entities/PressReleaseRelatedTransactionsTags'
import { RelatedTransactions } from './RelatedTransactions'
import { PressReleaseEntity } from '@finance-lab/utils/types'
import Breadcrumbs from '@mui/material/Breadcrumbs'
import Typography from '@mui/material/Typography'
import useAuth from '../../../../hooks/useAuth'
import Stack from '@mui/material/Stack'
import Button from '@mui/material/Button'
import { useQuery } from '../../../../hooks/useQuery'
import { CompanyTargets } from '../../../admin/pr-input/CompanyTargets'
import PRInputsApi from '../../../../api/PRInputsApi'
import { PressReleaseLanguageToggle } from '../../../../components/press-releases/PressReleaseLanguageToggle'

const PaperWrapper = ({ children }: { children: React.ReactNode }) => {
    return (
        <Paper sx={{ maxWidth: '100%', minWidth: 0, padding: 4, width: '100%', img: { maxWidth: '100%' } }}>
            {children}
        </Paper>
    )
}

const prinputsApi = new PRInputsApi()

const pressReleaseBodySx = {
    marginTop: 4,
    maxWidth: '100%',
    minWidth: 0,
    width: '100%',
    '& *': {
        maxWidth: '100%',
        minWidth: '0',
        overflowWrap: 'break-word',
        whiteSpace: 'normal',
        wordBreak: 'break-word',
    },
    '& table': {
        tableLayout: 'fixed',
        width: '100%',
    },
    ol: { listStylePosition: 'inside' },
    ul: { listStylePosition: 'inside' },
} as const

export const PressRelease = () => {
    const { t } = useTranslation()
    const { language: locale } = useAppLocale()
    const pressRelease = useLoaderData<PressReleaseEntity>()
    const revalidator = useRevalidator()
    const { user } = useAuth()
    const isSuperAdmin = user?.groups?.includes('superadmin')
    const query = useQuery()
    const isPrInputMode = query.get('prInputMode') === 'true'

    const bodyContent = pressRelease.body
    const publishDate = pressRelease.publishedAt
    const attachments = pressRelease.attachments.map((a) => ({
        ...a,
        caption: a.caption ?? a.url.split('/').pop(),
    }))

    const handleArchive = async (pr: { id: string }) => {
        await prinputsApi
            .archive(pr)
            .then(() => console.log(`Archived ${pr.id}`))
            .then(revalidator.revalidate)
            .catch((error) => console.error(error))
    }
    const prCompany = React.useMemo(
        () => ({ name: pressRelease.companyName, id: pressRelease.companyId }),
        [pressRelease.companyId, pressRelease.companyName],
    )

    return (
        <>
            <Helmet title={pressRelease.title} />
            <Grid container spacing={4} sx={{ minWidth: 0, width: '100%' }}>
                <Grid
                    size={{ md: 8 }}
                    sx={{
                        justifyItems: 'center',
                        minWidth: 0,
                    }}
                >
                    <PaperWrapper>
                        <Box sx={{ display: 'flex', maxWidth: '100%' }}>
                            <Breadcrumbs
                                aria-label='breadcrumb'
                                sx={{
                                    width: 'calc(100%)',
                                    whiteSpace: 'nowrap',
                                    textOverflow: 'ellipsis',
                                    overflow: 'hidden',
                                    ol: {
                                        flexWrap: 'nowrap',
                                        'li:last-of-type': {
                                            overflow: 'hidden',
                                        },
                                    },
                                }}
                            >
                                <Link color='inherit' to='/companies'>
                                    {t('entities.company.plural')}
                                </Link>
                                <Link color='inherit' to={`/companies/${pressRelease.companyId}`}>
                                    {pressRelease.companyName}
                                </Link>
                                <Typography
                                    sx={{ color: 'text.primary', textOverflow: 'ellipsis', overflow: 'hidden' }}
                                >
                                    {pressRelease.title}
                                </Typography>
                            </Breadcrumbs>
                            <Box sx={{ flexGrow: 1 }}>
                                <PressReleaseLanguageToggle
                                    companyId={pressRelease.companyId}
                                    translations={pressRelease.translations}
                                />
                            </Box>
                        </Box>
                        <h1>{pressRelease.title}</h1>

                        <Stack direction={'row'} sx={{ justifyContent: 'space-between' }}>
                            <Box>
                                <Box
                                    sx={{
                                        display: 'inline-flex',
                                        flexDirection: 'row',
                                        gap: '4px',
                                        alignItems: 'center',
                                    }}
                                >
                                    <ClockIcon size={16} />
                                    {formatDateYYYYMMDDhhmmss(publishDate, { locale })}
                                </Box>

                                <Box sx={{ marginTop: 2 }}>
                                    <PressReleaseRelatedTransactionsTags
                                        relatedTransactions={pressRelease.relatedTransactions}
                                    />
                                </Box>
                            </Box>
                            <Box>
                                {isSuperAdmin && (
                                    <Button
                                        component={Link}
                                        color='primary'
                                        variant='contained'
                                        size='small'
                                        to={isPrInputMode ? '?' : '?prInputMode=true'}
                                        sx={{ '&:visited': { color: 'white' } }}
                                    >
                                        {isPrInputMode
                                            ? t('pages.companies.pressRelease.listView')
                                            : t('pages.companies.pressRelease.linkMode')}
                                    </Button>
                                )}
                            </Box>
                        </Stack>

                        <Box sx={pressReleaseBodySx} dangerouslySetInnerHTML={{ __html: bodyContent }} />

                        <RelatedTransactions transactions={pressRelease.relatedTransactions} />

                        {attachments.length > 0 && (
                            <div>
                                <List
                                    subheader={
                                        <ListSubheader sx={{ paddingLeft: 0 }}>
                                            {t('pages.companies.pressRelease.linkedFiles')}
                                        </ListSubheader>
                                    }
                                >
                                    {attachments.map(({ caption, url }) => (
                                        <ListItemButton
                                            key={url}
                                            component='a'
                                            href={url}
                                            target='_blank'
                                            sx={{ paddingLeft: 0 }}
                                        >
                                            <ListItemIcon sx={{ minWidth: '36px' }}>
                                                <FileIcon />
                                            </ListItemIcon>
                                            <ListItemText primary={caption || url} />
                                        </ListItemButton>
                                    ))}
                                </List>
                            </div>
                        )}
                    </PaperWrapper>
                </Grid>
                <Grid size={{ md: 4 }}>
                    {!isPrInputMode && (
                        <CompanyPressReleases companyId={pressRelease.companyId} currentPrId={pressRelease.id} />
                    )}
                    {isPrInputMode && (
                        <CompanyTargets
                            selectedCompany={prCompany}
                            selectedPR={pressRelease}
                            onArchivePR={handleArchive}
                        />
                    )}
                </Grid>
            </Grid>
        </>
    )
}
